import { Component, AfterViewInit, OnDestroy, Input, ViewEncapsulation, ViewChild, ElementRef, NgZone } from '@angular/core';
import { Marker, Map, LngLatLike } from 'mapbox-gl';
import { Point } from 'geojson';

import { MoteService, UserPositionService, MapService, MoteEventService } from '../../providers';
import { User, Coords } from '../../models';
import { AutoSubOnDestroy, AutoSub } from '../../providers/auto-sub';
import { fromEvent, ReplaySubject, animationFrameScheduler } from 'rxjs';
import { throttleTime, first, scan, distinctUntilChanged } from 'rxjs/operators';
import { GyroService } from '../../providers/gyro.service';

@AutoSubOnDestroy()
@Component({
    selector: 'user-map-marker',
    templateUrl: './user-map-marker.component.html',
    styleUrls: ['./user-map-marker.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UserMapMarkerComponent implements AfterViewInit, OnDestroy {

    @ViewChild('userMarkerContainer', { static: true }) markerContainer: ElementRef;
    @ViewChild('directionConeContainer', { static: true }) directionConeContainer: ElementRef;
    @ViewChild('userMarkerCore', { static: true }) markerCore: ElementRef;

    @Input("enableDragging") set enableDragging(enable: boolean) {
        this.enableDrag(enable);
    }

    private enabled = true;

    public key: string = "user_";
    public heading: number = 0;
    public lastHeading: number = 0;
    public showHeading: boolean = false;
    public isInitAnimationRunning: boolean = false;

    private map: Map;
    private mapboxMarker: Marker = null;
    private latestUser: User = null;
    private isDragging: boolean = false;

    private initSubject: ReplaySubject<void> = new ReplaySubject(1);

    constructor(private ngZone: NgZone, private mapService: MapService, private userPositionService: UserPositionService, private moteService: MoteService, private gyroService: GyroService, private moteEventService: MoteEventService) {
    }

    ngAfterViewInit() {
        // Wait for map to init
        this.mapService.awaitInit().then((map) => {
            this.map = map;
            this.initWatch();
        });
    }

    private initWatch() {
        // Watch map clicks
        AutoSub(this).reg['clickSub'] = this.mapService.watchMapClick().subscribe(async features => {
            if(!this.enabled) return;
        });

        let mapZoom = fromEvent(this.mapService.map, 'zoom');

        AutoSub(this).reg['zoomSub'] = mapZoom.subscribe(() => {
            if(!this.enabled) return;
            this.updateUserRadius(this.latestUser);
        });

        // Watch user pos
        AutoSub(this).reg['userPos'] = this.userPositionService.watchUserPosition().subscribe(user => {
            if(!this.enabled) return;
            if(this.isDragging) return;

            this.latestUser = user;
            this.updateUserMarker(user);
            this.updateUserRadius(user);
        });

        this.watchGyroOutsideAngular();
    }

    private watchGyroOutsideAngular() {
        // Run fast gyro updates outside angular zone
        // This reduces CPU usage from 50% -> ~5% in 'idle' state (in debug build)
        // Using timer of 30ms (meaning ~every second animation frame) reduces idle CPU to 1% (in debug build)
        let isHeadinInitialized = false;

        // Filter out ghost movements (< 0.5 deg) from shaking hand to make mark look more steady
        let ghostFilter = scan<number>((prev, curr) => (Math.abs(prev - curr) >= 0.05) ? curr : prev);

        this.ngZone.runOutsideAngular(() => {
            AutoSub(this).reg['gyroSub'] = this.gyroService.watchGyroMapHeading(this.mapService).pipe(ghostFilter, distinctUntilChanged()).subscribe(heading => {
                // Wait to fetch new headings (and override core and cone rotation) until init-animation is done
                if(this.isInitAnimationRunning) return;

                this.lastHeading = this.heading;
                this.heading = this.gyroService.closestEquivalentAngle(this.lastHeading, heading);

                if(this.directionConeContainer && this.markerCore) {
                    this.markerCore.nativeElement.style.setProperty('transform', `rotate(${this.heading}deg )`);
                    this.directionConeContainer.nativeElement.style.setProperty('transform', `rotate(${this.heading}deg )`);
                }

                // Mark heading as valid on first run
                // Only mark it valid after first non 0 value (desktop will set it to 0 once);
                let isHeadingValid = heading > 0 ? true : false;

                // Update heading valid inside of zone if it changed
                if(isHeadingValid && !isHeadinInitialized) {
                    isHeadinInitialized = true;
                    this.ngZone.run(() => this.showHeading = true);

                    // Override first heading scss variable (used in init-animation)
                    this.markerContainer.nativeElement.style.setProperty('--first-heading', this.heading );
                    // Start init-animation (2x360 deg rotation of core and cone)
                    this.isInitAnimationRunning = true;
                    setTimeout(() => { this.isInitAnimationRunning = false }, 5000);
                }
            })
        });
    }

    private async enableDrag(enable: boolean) {
        await this.initSubject.pipe(first()).toPromise();
        // Dragging of mapbox marker
        this.mapboxMarker.setDraggable(enable);
        // Make user radius bigger
        // this.userPositionService.setUserRadius(200);
        this.updateUserRadius(this.latestUser);

        // Listen for drag events
        let watchUserDragStart = fromEvent(this.mapboxMarker, 'dragstart');
        let watchUserDrag = fromEvent(this.mapboxMarker, 'drag').pipe(throttleTime(300, animationFrameScheduler, { leading: true, trailing: true }));
        let watchUserDragEnd = fromEvent(this.mapboxMarker, 'dragend');

        AutoSub(this).reg['userDragEndSub'] = watchUserDragEnd.subscribe(ev => {
            this.isDragging = false;
            this.updateUserPositionFromMarker();
        });

        AutoSub(this).reg['userDragSub'] = watchUserDrag.subscribe(ev => {
            this.updateUserPositionFromMarker();
        });

        AutoSub(this).reg['userDragStartSub'] = watchUserDragStart.subscribe(ev => {
            this.isDragging = true;
            this.updateUserPositionFromMarker();
        });

    }

    private updateUserPositionFromMarker() {
        if(!this.mapboxMarker) return;
        // Update user position
        let pos = this.mapboxMarker.getLngLat();
        let coords = new Coords(pos.lat, pos.lng, true);

        if(!pos) return;
        this.userPositionService.setUserPosition(coords, this.latestUser);
    }

    private updateUserRadius(user: User): number {
        if(!user) return;
        // Calculate user circle radius
        const currentZoom = this.mapService.map.getZoom();
        // TODO, is zoom calc wrong in old app or user radius (hench why do we need to add a factor 4?)
        const unlockRadius = this.metersToPixes(user.position.lat, user.userRadius * 4, currentZoom);
        // Update unlock radius in view
        if(!this.markerContainer) return;
        this.ngZone.runOutsideAngular(() => {
            this.markerContainer.nativeElement.style.setProperty('width', `${unlockRadius}px`);
            this.markerContainer.nativeElement.style.setProperty('height', `${unlockRadius}px`);
        })
    }

    private updateUserMarker(user: User) {
        if(!user || !user.position.isValid) {
            return;
        }
        // Add MapBox marker
        let position: Point = { type: "Point", coordinates: user.position.longLat() };
        this.updateMapboxMarker(position);
    }

    private updateMapboxMarker(position: Point) {
        if(this.mapboxMarker) {
            // Just update the position and then return
            this.mapboxMarker.setLngLat(<LngLatLike>position.coordinates).addTo(this.map);
            return;
        }

        if(!position) return;

        // Create a new marker
        this.markerContainer.nativeElement.style.display = "flex";
        this.mapboxMarker = new Marker(this.markerContainer.nativeElement, { draggable: false, offset: [0, 0] });
        this.mapboxMarker.setLngLat(<LngLatLike>position.coordinates).addTo(this.map);

        // Mark as initialized when first user position has been received 
        // and its ok to refere to map user Marker object
        this.initSubject.next();
    }

    private metersPerPixel(latitude, zoomLevel): number {
        const earthCircumference = 40075017;
        let latitudeRadians = latitude * (Math.PI / 180);
        return earthCircumference * Math.cos(latitudeRadians) / Math.pow(2, zoomLevel + 8);
    }

    private metersToPixes(latitude, meters, zoomLevel): number {
        return meters / this.metersPerPixel(latitude, zoomLevel);
    }

    public ngOnDestroy() {
        if(this.mapboxMarker) this.mapboxMarker.remove();
    }
}
